/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What each field on an artifact is _about_** (`#1322`, ruled by Todd 2026-09-15).
 *
 * `#1307` asked three questions of every offered field, and the third was *is this metadata about
 * the artifact, or content of it?* A demand source's `floorCpm` is **of** it — it is what the source
 * is. Its `createdBy` is **about** it — who wrote the record. The census of that classification lived
 * in a measurement report, which is a comment nothing can read (`#1190`).
 *
 * **Declared on the artifact rather than on one type's schema.** Every field an artifact type
 * inherits from `ArtifactDto` is a property of *artifacts* — identity, evidence, lineage, lifecycle,
 * ownership, authorship, audit — and every such field is metadata about the artifact wherever it
 * appears. So the classification is keyed by `ArtifactDto`'s own fields, and
 * `artifact-field-class.spec.ts` fails when the two disagree in either direction: the next field
 * promoted to the base cannot arrive unclassified, and nothing here can outlive a field that left.
 * A second artifact type offered to mappings is then an addition, never a second census.
 *
 * **Fields a type declares for itself are _of_ it by construction**, and are not listed. That is the
 * measurement, not a default: `#1307`'s census found no type-specific field on a demand source that is
 * about the record rather than the source.
 *
 * `description` and `tags` are **about** (Todd, 2026-09-15). The census had filed them as *of*; the
 * base's own docblocks call both properties of artifacts, and neither is something a partner's request
 * expects.
 *
 * **What this does not decide.** Todd's *internal versus descriptive* distinction, and what a `when`
 * criteria may offer, are `#1308`'s. This states what a field **is**; whether it may go somewhere is
 * the consumer's question. {@link ArtifactFieldDeclaration.namesAUser} is a fact about the value —
 * the sentence an operator reads says so — and deliberately not a ruling on where it may be sent.
 *
 * **Imports nothing**, because the console aliases it (`shared-modules.spec.ts`).
 */

/** The kind of fact an artifact-level field records about the artifact. */
export type ArtifactFieldClass =
  | 'identity'
  | 'evidence'
  | 'lineage'
  | 'lifecycle'
  | 'visibility'
  | 'ownership'
  | 'authorship'
  | 'audit'
  | 'annotation';

export interface ArtifactFieldDeclaration {
  readonly class: ArtifactFieldClass;
  /**
   * **The value identifies a user of this system** — an owner or an author.
   *
   * Stated because an operator mapping it onto a request is putting internal identity into a third
   * party's hands, and a warning that does not say so describes our mechanism rather than their
   * outcome (`#1045`).
   */
  readonly namesAUser?: true;
}

/**
 * One entry per `ArtifactDto` field, compared against the type in both directions by
 * `artifact-field-class.spec.ts`.
 */
export const ARTIFACT_FIELD_CLASS = Object.freeze({
  id: { class: 'identity' },
  name: { class: 'identity' },
  objectId: { class: 'identity' },
  parentId: { class: 'lineage' },
  origin: { class: 'lineage' },
  removedFromParent: { class: 'lineage' },
  validation: { class: 'evidence' },
  status: { class: 'lifecycle' },
  enabled: { class: 'lifecycle' },
  isTemplate: { class: 'lifecycle' },
  visibility: { class: 'visibility' },
  ownerId: { class: 'ownership', namesAUser: true },
  // Who manages it (`OT5`, slice `3d`): ownership, and it names no user — `system` or `owner`.
  managedBy: { class: 'ownership' },
  authoring: { class: 'authorship' },
  createdBy: { class: 'authorship', namesAUser: true },
  updatedBy: { class: 'authorship', namesAUser: true },
  createdAt: { class: 'audit' },
  updatedAt: { class: 'audit' },
  description: { class: 'annotation' },
  tags: { class: 'annotation' },
} as const satisfies Record<string, ArtifactFieldDeclaration>);

/**
 * The declaration for a top-level artifact field, or `undefined` when the field is the type's own.
 *
 * Own-property only, so `constructor` or `toString` is never mistaken for a declared field.
 */
export function artifactFieldDeclaration(field: string): ArtifactFieldDeclaration | undefined {
  return Object.prototype.hasOwnProperty.call(ARTIFACT_FIELD_CLASS, field)
    ? (ARTIFACT_FIELD_CLASS as Readonly<Record<string, ArtifactFieldDeclaration>>)[field]
    : undefined;
}
